import { useAuth } from './useAuth';
import { useAdminSessionValidation } from './useAdminAuth';
import { IAdmin, IAuthUser } from '@order-up/models';

/**
 * Hook to check if the current authenticated user has admin privileges.
 *
 * Admin status is determined entirely from the server-validated admin
 * session, which reads `admins/{uid}` (the single source of truth). The
 * server returns the IAdmin record on success, or 403 (mapped to `null`)
 * when the user is not — or no longer is — an active platform admin. So
 * `isAdmin` is just `!!admin` here.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isAdmin, isLoading } = useCurrentUserAdmin();
 *   if (isLoading) return <div>Loading...</div>;
 *   return isAdmin ? <AdminPanel /> : <RegularUserPanel />;
 * }
 * ```
 */
export function useCurrentUserAdmin(): {
  isAdmin: boolean;
  isLoading: boolean;
  admin: IAdmin | null;
  authUser: IAuthUser | null;
} {
  const { data: authData } = useAuth();
  const { authUser, authResolved } = authData;

  const { data: admin, isLoading: isSessionLoading } = useAdminSessionValidation();

  // `useAuth().isLoading` is permanently false (the query carries initialData),
  // so it never covered the window where Firebase has not yet restored the
  // persisted session. `authResolved` is what actually reports that.
  const isLoading = !authResolved || isSessionLoading;
  const isAdmin = Boolean(admin);

  return {
    isAdmin,
    isLoading,
    admin: admin || null,
    authUser: authUser || null,
  };
}
